import { useState } from 'react'

import { BlankSlate, Box, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import { MultipleChoiceFilter } from 'generated/resources/datatable.graphql'
import {
  NineBoxGridDocument,
  PerformanceEmployeePerformanceInsightsSummaryFilter,
  PerformanceReviewEmployeeScoreGrid,
} from 'generated/resources/graphql'
import { xor } from 'lodash'

import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { Filter } from 'modules/core/components/DataTable/types'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  emptyStateBackground,
  emptyStateContainer,
} from 'modules/performance_analytics/components/NineBoxGrid/index.vanilla.css'

import { GRID_FILTER_IDENTIFIER } from '../PerformanceInsightsSummaryDatatable/filters/gridFilter'
import WithToolTip from '../WithTooltip'
import { GridItem, NineBoxGridPosition } from './GridItem'
import Sidepanel from './Sidepanel'
import { Skeleton } from './Skeleton'

export const NineBoxGrid = () => {
  const {
    getFiltersVariables,
    availableFilters,
    filters: enabledFilters,
    onUpdateFilter,
    onAddFilter,
  } = useFilters()
  const dataTableFilters =
    // eslint-disable-next-line no-type-assertion/no-type-assertion -- DataTable types are not very precise
    (getFiltersVariables().filter as PerformanceEmployeePerformanceInsightsSummaryFilter) ?? {}

  const { data, previousData } = useQuery(NineBoxGridDocument, {
    variables: {
      employeeIds: dataTableFilters.employeeId?.in,
      managerIds: dataTableFilters.managerId?.in,
      scoreRange: dataTableFilters.scoreRange?.in,
      teamIds: dataTableFilters.teamId?.in,
      tenureDate: dataTableFilters.tenureDate?.in,
      potentialScoreRange: dataTableFilters.potentialScoreRange?.in,
      genders: dataTableFilters.gender?.in,
      workspaceIds: dataTableFilters.workspace?.in,
      legalEntityIds: dataTableFilters.legalEntityId?.in,
      nationalities: dataTableFilters.nationality?.in,
      projectIds: dataTableFilters.project?.in,
    },
  })

  const loadedData = data || previousData

  const reviewEmployeeScoreGrid =
    loadedData?.performance?.reviewEmployeeScoreGridsConnection?.nodes || []
  const hasNineboxData = !!loadedData?.performance.nineboxWithoutFilter.nodes.length

  const [openSidepanel, showSidePanel] = useState(false)
  const totalEmployees = reviewEmployeeScoreGrid.reduce(
    (total, value) => (total += value.totalCount),
    0
  )

  const employeesByGridScore: Record<string, PerformanceReviewEmployeeScoreGrid> =
    reviewEmployeeScoreGrid.reduce((all, value) => {
      return {
        ...all,
        [value.category]: value,
      }
    }, {})

  const gridFilterEmpty =
    !dataTableFilters.gridStatus?.in || dataTableFilters.gridStatus.in.length === 0
  const noData = !!loadedData && !hasNineboxData

  const gridFilterHasPosition = (position: NineBoxGridPosition) => {
    if (gridFilterEmpty) return true
    return dataTableFilters.gridStatus?.in?.includes(position) ?? false
  }

  const onGridCellClick = (position: NineBoxGridPosition) => {
    const { gridFilter, selectedOption, filterAlreadyEnabled } =
      getSelectedFilterAndOptionByPosition(position, enabledFilters, availableFilters)

    if (!gridFilter || !selectedOption) {
      return
    }
    const currentValue = dataTableFilters.gridStatus?.in || []
    // Toggle the selected grid position: add if not present, remove if already selected
    const nextValue = xor(currentValue, [selectedOption.value])
    const nextFilter = { ...gridFilter, value: nextValue }

    // Prevent dropdown auto-opening when applying filter programmatically
    // Example: when user clicks grid cell instead of filter dropdown directly
    nextFilter.noAutoOpen = true

    if (filterAlreadyEnabled) {
      onUpdateFilter(nextFilter)
      return
    }

    onAddFilter(nextFilter)
  }

  return (
    <Box
      border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
      borderRadius={{ all: 'abs010' }}
      position="relative"
      minHeight="s400"
      justifyContent="center"
    >
      <Sidepanel open={openSidepanel} onClose={() => showSidePanel(false)} />

      <Box flexDirection="row" padding="s16" justifyContent="spaceBetween">
        <Box flexDirection="row" gap="s8">
          <Text.Normal size="200" weight="bold">
            {i18n.t('performance_process.analytics.nine_box_grid.chart.title')}
          </Text.Normal>
          <Text.Normal size="200">
            {i18n.tp('performance_process.analytics.nine_box_grid.chart.employee_count', {
              count: totalEmployees,
            })}
          </Text.Normal>
          <WithToolTip label={i18n.t('performance_process.analytics.nine_box_grid.chart.tooltip')}>
            <Icon.Medium icon="alert-circle-line" />
          </WithToolTip>
        </Box>
        <Touchable onClick={() => showSidePanel(true)}>
          <Icon.Medium icon="arrow-right" />
        </Touchable>
      </Box>

      <Box position="relative">
        <Box
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          gap="s12"
          style={{
            top: '45%',
            left: '0px',
            position: 'absolute',
            transform: 'rotate(270deg) translate(-40%, 60%)',
            transformOrigin: 'top left',
          }}
        >
          <Text.Normal size="200">
            {i18n.t('performance_process.analytics.nine_box_grid.chart.potential_score')}
          </Text.Normal>
          <Icon.Small icon="arrow-right" />
        </Box>

        {/* @ts-expect-error - inert is not yet in the DOM typings but is valid HTML */}
        <div inert={!hasNineboxData ? 'true' : undefined}>
          {loadedData ? (
            <Box paddingLeft="s48" paddingBottom="s32">
              <Box flexDirection="row">
                <GridItem
                  position="7"
                  scoreGrid={employeesByGridScore['7']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('7')}
                  isActive={gridFilterHasPosition('7')}
                  showPercentage={gridFilterEmpty}
                />
                <GridItem
                  position="8"
                  scoreGrid={employeesByGridScore['8']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('8')}
                  isActive={gridFilterHasPosition('8')}
                  showPercentage={gridFilterEmpty}
                />
                <GridItem
                  position="9"
                  scoreGrid={employeesByGridScore['9']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('9')}
                  isActive={gridFilterHasPosition('9')}
                  showPercentage={gridFilterEmpty}
                />
              </Box>

              <Box flexDirection="row">
                <GridItem
                  position="4"
                  scoreGrid={employeesByGridScore['4']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('4')}
                  isActive={gridFilterHasPosition('4')}
                  showPercentage={gridFilterEmpty}
                />
                <GridItem
                  position="5"
                  scoreGrid={employeesByGridScore['5']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('5')}
                  isActive={gridFilterHasPosition('5')}
                  showPercentage={gridFilterEmpty}
                />
                <GridItem
                  position="6"
                  scoreGrid={employeesByGridScore['6']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('6')}
                  isActive={gridFilterHasPosition('6')}
                  showPercentage={gridFilterEmpty}
                />
              </Box>

              <Box flexDirection="row">
                <GridItem
                  position="1"
                  scoreGrid={employeesByGridScore['1']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('1')}
                  isActive={gridFilterHasPosition('1')}
                  showPercentage={gridFilterEmpty}
                />
                <GridItem
                  position="2"
                  scoreGrid={employeesByGridScore['2']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('2')}
                  isActive={gridFilterHasPosition('2')}
                  showPercentage={gridFilterEmpty}
                />
                <GridItem
                  position="3"
                  scoreGrid={employeesByGridScore['3']}
                  isEmpty={noData}
                  totalEmployees={totalEmployees}
                  onClick={() => onGridCellClick('3')}
                  isActive={gridFilterHasPosition('3')}
                  showPercentage={gridFilterEmpty}
                />
              </Box>
            </Box>
          ) : (
            <Skeleton />
          )}
        </div>

        <Box
          position="relative"
          flexDirection="row"
          flexWrap="noWrap"
          justifyContent="center"
          alignItems="center"
          gap="s12"
          style={{ top: '-15px' }}
        >
          <Text.Normal size="200">
            {i18n.t('performance_process.analytics.nine_box_grid.chart.performance_score')}
          </Text.Normal>
          <Icon.Small icon="arrow-right" />
        </Box>

        {noData && (
          <Box className={emptyStateBackground}>
            <Box className={emptyStateContainer}>
              <BlankSlate
                title={i18n.t(
                  'performance_process.analytics.nine_box_grid.chart.blank_slate.title'
                )}
                description={i18n.t(
                  'performance_process.analytics.nine_box_grid.chart.blank_slate.description'
                )}
                illustration="analytics-graph"
                size="small"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

function getSelectedFilterAndOptionByPosition(
  position: NineBoxGridPosition,
  enabledFilters: Filter<unknown>[],
  availableFilters: Filter<unknown>[]
) {
  const nineBoxFinder = (filter: Filter<unknown>) =>
    filter.filter.identifier === GRID_FILTER_IDENTIFIER

  const gridFilterAvailable: Filter<unknown> | undefined = availableFilters.find(nineBoxFinder)
  const gridFilterEnabled: Filter<unknown> | undefined = enabledFilters.find(nineBoxFinder)
  const gridFilter = gridFilterAvailable || gridFilterEnabled
  // eslint-disable-next-line no-type-assertion/no-type-assertion -- DataTable types are not flexible enough
  const selectedOption = (gridFilter?.category as MultipleChoiceFilter<string>).options.find(
    (option) => option.value === position
  )

  return { gridFilter, selectedOption, filterAlreadyEnabled: !!gridFilterEnabled }
}
